import React, { useState, useEffect } from "react";
import { Button, Modal, Input, Select, message, Alert, Divider } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import NumberFormat from "react-number-format";
import { DateTime } from "luxon";
import { roundDisplayPrice } from "../../../utils/priceHelpers";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  _get_restaurant_bikers_list,
  _confirm_pickup,
} from "../../../common/axios_services";

const OrderModals = ({
  isModalVisible,
  setIsModalVisible,
  isAcceptModalVisible,
  setIsAcceptModalVisible,
  isReadyModal,
  setIsReadyModal,
  selectedOrder,
  activeTab,
  handleAcceptOrder,
  handleRejectOrder,
  handleReadyOrder,
  handleConfirmPickup,
}) => {
  const navigate = useNavigate();
  const userData = useSelector((state) => state.userProfile.dataFetched);
  const restaurantId = userData?.restaurantId;

  const isScheduledTab = activeTab === "pending_delivery";
  const isTodayTab = activeTab === "todays_delivery";
  const isAllTab = activeTab === "todays_sales";

  const [bikerId, setBikerId] = useState("");
  const [bikers, setBikers] = useState([]);
  const [pickupModalVisible, setPickupModalVisible] = useState(false);
  const [pickupCode, setPickupCode] = useState("");
  const [selectedPickupOrder, setSelectedPickupOrder] = useState(null);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [acceptDeliveryModalVisible, setAcceptDeliveryModalVisible] =
    useState(false);
  const deliveryFee = selectedOrder?.deliveryFeeEarnings?.deliveryRestaurantFee;

  useEffect(() => {
    fetchBikers();
  }, [selectedOrder]);

  const fetchBikers = async () => {
    if (restaurantId) {
      try {
        const response = await _get_restaurant_bikers_list(restaurantId, {
          page: 1,
          pageSize: 100,
        });
        setBikers(response.data?.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch bikers:", error);
      }
    }
  };

  const handleReadyClick = () => {
    handleReadyOrder(bikerId || null);
    setBikerId("");
  };

  const handlePickupOrder = (order) => {
    setSelectedPickupOrder(order);
    setPickupModalVisible(true);
  };

  const confirmPickup = async () => {
    if (!selectedPickupOrder || !pickupCode.trim()) {
      message.error("Please enter a pickup code");
      return;
    }

    try {
      await _confirm_pickup(selectedPickupOrder.id, pickupCode.trim());
      message.success("Order picked up successfully!");
      setPickupModalVisible(false);
      setPickupCode("");
      setSelectedPickupOrder(null);
      handleConfirmPickup();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to confirm pickup. Please check the code.",
      );
    }
  };

  const confirmRejectOrder = () => {
    setRejectModalVisible(false);
    handleRejectOrder();
  };

  const confirmAcceptDelivery = () => {
    setAcceptDeliveryModalVisible(false);
    setRejectModalVisible(false);
    handleAcceptOrder(true);
  };

  // Group items by deliveryDateType
  const groupedItems =
    selectedOrder?.orderItems?.reduce((acc, item) => {
      const deliveryType = item.deliveryDateType || "same_day";
      if (!acc[deliveryType]) {
        acc[deliveryType] = [];
      }
      acc[deliveryType].push(item);
      return acc;
    }, {}) || {};

  const renderOrderSection = (groupKey, items) => {
    const isNextDay = groupKey === "next_day";
    const deliveryDate = isNextDay
      ? selectedOrder.scheduledDeliveryDate
        ? new Date(selectedOrder.scheduledDeliveryDate).toLocaleDateString()
        : "TBD"
      : "Today";

    const sectionTitle = isNextDay
      ? `ORDER TO ${selectedOrder.deliveryType === "DELIVERY" ? "DELIVER" : "PICKUP"
      } ON: ${deliveryDate}`
      : `ORDER TO ${selectedOrder.deliveryType === "DELIVERY" ? "DELIVER" : "PICKUP"
      } TODAY`;

    const groupTotal = items.reduce((sum, item) => {
      const itemTotal = parseFloat(item.price) * item.quantity;
      const containerCost = parseFloat(item.menuItem?.containerCost || 0);
      const portionsPerPack = item.menuItem?.portionsPerPack || 1;
      const numContainers = containerCost > 0 ? Math.ceil(item.quantity / portionsPerPack) : 0;
      return sum + itemTotal + (containerCost * numContainers);
    }, 0);

    return (
      <div
        key={groupKey}
        className="mb-8 pb-8 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0"
      >
        <h3 className="text-2xl font-bold text-gray-900 mb-6">
          {sectionTitle}
        </h3>

        <div className="mb-6 space-y-3">
          <h4 className="text-2xl font-semibold text-gray-800 mb-3">
            Order Items
          </h4>
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            {items.map((item, index) => {
              const containerCost = parseFloat(item.menuItem?.containerCost || 0);
              const portionsPerPack = item.menuItem?.portionsPerPack || 1;
              const numContainers = containerCost > 0 ? Math.ceil(item.quantity / portionsPerPack) : 0;
              const containerTotal = containerCost * numContainers;
              return (
                <div key={index} className="pb-3 border-b border-gray-200 last:border-b-0 last:pb-0">
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <p className="text-2xl font-medium text-gray-900">
                        {item.quantity}x {item.menuItem?.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        ₦{parseFloat(item.price).toFixed(2)} per unit
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-[#44843F] ml-4">
                      ₦{(parseFloat(item.price) * item.quantity).toFixed(2)}
                    </p>
                  </div>
                  {containerTotal > 0 && (
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-600">
                        Container Cost (x{numContainers})
                      </p>
                      <p className="text-sm font-semibold text-[#44843F] ml-4">
                        ₦{containerTotal.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-6 space-y-3">
          <h4 className="text-2xl font-semibold text-gray-800 mb-3">
            {selectedOrder.deliveryType === "DELIVERY"
              ? "DELIVERY ADDRESS"
              : "PICKUP ADDRESS"}
          </h4>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-2xl text-gray-900">
              {selectedOrder.deliveryType === "DELIVERY"
                ? selectedOrder.delivery?.address ||
                selectedOrder.savedAddress?.address ||
                "Customer Address"
                : selectedOrder.restaurant?.businessInfo?.pickupAddress ||
                "Restaurant Address"}
            </p>
          </div>
        </div>

        {selectedOrder.deliveryType === "DELIVERY" &&
          selectedOrder?.delivery?.restaurantAcceptedDelivery === false && (
            <div className="mb-6">
              <div className="flex justify-between items-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                <span className="text-2xl font-semibold text-gray-800">
                  Delivery Fee:
                </span>
                <span className="text-xl font-bold text-orange-600">
                  <span className="small_currency">NGN</span>{" "}
                  <NumberFormat
                    thousandSeparator={true}
                    prefix={""}
                    className="numeric"
                    displayType={"text"}
                    value={parseFloat(deliveryFee || "0.00").toFixed(2)}
                  />{" "}
                </span>
              </div>
            </div>
          )}

        {selectedOrder.biker && selectedOrder.deliveryType === "DELIVERY" && (
          <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="text-2xl font-semibold text-purple-900 mb-3">
              🏍️ Delivery Person
            </h4>
            <div className="space-y-2">
              <div>
                <span className="text-purple-700 font-medium">Name:</span>
                <span className="ml-2 text-gray-900">
                  {selectedOrder.biker?.user?.firstName}{" "}
                  {selectedOrder.biker?.user?.lastName}
                </span>
              </div>
              <div>
                <span className="text-purple-700 font-medium">Phone:</span>
                <span className="ml-2 text-gray-900">
                  {selectedOrder.biker?.user?.phoneCode}
                  {selectedOrder.biker?.user?.phoneNumber || "N/A"}
                </span>
              </div>
            </div>
          </div>
        )}

        {selectedOrder?.delivery?.restaurantAcceptedDelivery === true && (
          <div className="flex justify-between items-center p-4 bg-orange-50 rounded-lg border border-orange-200 mb-3">
            <span className="text-2xl font-semibold text-gray-800">
              Restaurant Delivery Fee:
            </span>
            <span className="text-2xl font-bold text-orange-600">
              <span className="small_currency">NGN</span>{" "}
              <NumberFormat
                thousandSeparator={true}
                prefix={""}
                className="numeric"
                displayType={"text"}
                value={parseFloat(
                  selectedOrder?.restaurantFee ||
                  selectedOrder?.pricing?.restaurantFee ||
                  selectedOrder?.pricing?.serviceFee ||
                  selectedOrder?.serviceFee ||
                  selectedOrder?.deliveryFeeEarnings?.deliveryRestaurantFee ||
                  deliveryFee ||
                  0
                ).toFixed(2)}
              />{" "}
            </span>
          </div>
        )}

        <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg border border-green-200">
          <span className="text-2xl font-semibold text-gray-800">
            AMOUNT YOU WILL GET:
          </span>
          <span className="text-2xl font-bold text-green-600">
            <span className="small_currency">NGN</span>{" "}
            <NumberFormat
              thousandSeparator={true}
              prefix={""}
              className="numeric"
              displayType={"text"}
              value={roundDisplayPrice(groupTotal)}
            />{" "}
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        title={
          <div className="flex justify-between items-center">
            <span>Order Details - #{selectedOrder?.orderCode}</span>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setIsModalVisible(false)}
            />
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
        }}
        footer={null}
        centered={true}
        destroyOnClose={true}
        width={700}
        className="order-details-modal"
        bodyStyle={{ overflowY: "auto", maxHeight: "70vh" }}
        closable={false}
      >
        {selectedOrder && (
          <div className="space-y-6">
            <div className="space-y-4 pb-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <p className="text-sm text-gray-600 font-semibold mb-1">
                    Order REF CODE:
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    #{selectedOrder.orderCode}
                  </p>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 font-semibold mb-1">
                    Order Time:
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {new Date(selectedOrder.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-2xl font-semibold text-blue-900 mb-3">
                Customer Information
              </h3>
              <div className="space-y-2">
                <div>
                  <span className="text-blue-700 font-medium">Name:</span>
                  <span className="ml-2 text-gray-900">
                    {selectedOrder.user?.firstName}{" "}
                    {selectedOrder.user?.lastName}
                  </span>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Telephone:</span>
                  <span className="ml-2 text-gray-900">
                    {selectedOrder.user?.phoneCode}{" "}
                    {selectedOrder.user?.phoneNumber}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600 font-semibold mb-1">
                Status:
              </p>
              <p className="text-xl font-bold text-gray-900">
                {selectedOrder.status?.toUpperCase()}
              </p>
            </div>

            <h3 className="text-2xl font-semibold">Order Details</h3>

            <div>
              {Object.keys(groupedItems).map((groupKey) =>
                renderOrderSection(groupKey, groupedItems[groupKey]),
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              {(selectedOrder.status?.toLowerCase() === "waiting" ||
                selectedOrder.status?.toLowerCase() === "pending") && (
                <>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => {
                      setIsModalVisible(false);
                      if (
                        selectedOrder.deliveryType?.toUpperCase() === "PICKUP"
                      ) {
                        handleAcceptOrder(false);
                      } else {
                        setIsAcceptModalVisible(true);
                      }
                    }}
                    className="px-8"
                  >
                    Accept
                  </Button>
                  <Button
                    danger
                    size="large"
                    onClick={handleRejectOrder}
                    className="px-8"
                  >
                    Reject
                  </Button>
                </>
              )}

              {(selectedOrder.status?.toLowerCase() === "accepted" ||
                selectedOrder.status?.toLowerCase() === "confirmed" ||
                selectedOrder.status?.toLowerCase() === "processing") && (
                <Button
                  type="primary"
                  size="large"
                  className="bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700 px-8"
                  onClick={() => {
                    setBikerId("");
                    setIsReadyModal(true);
                    setIsModalVisible(false);
                  }}
                >
                  Ready
                </Button>
              )}

              {(selectedOrder.status?.toLowerCase().includes("ready") ||
                selectedOrder.status?.toLowerCase() === "ready") && (
                <Button
                  type="primary"
                  size="large"
                  className="bg-yellow-600 hover:bg-yellow-700 border-yellow-600 hover:border-yellow-700 px-8"
                  onClick={() => {
                    handlePickupOrder(selectedOrder);
                    setIsModalVisible(false);
                  }}
                >
                  Pick-up
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Accept Modal with Dropdown */}
      <Modal
        title="Confirm Order Acceptance"
        open={isAcceptModalVisible}
        onCancel={() => setIsAcceptModalVisible(false)}
        footer={null}
        centered={true}
        width={500}
      >
        <div className="text-center space-y-6">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-2xl font-medium text-blue-900 mb-2">
              Delivery Options
            </p>
            <p className="text-gray-700">
              Do you want to handle the delivery for this order?
            </p>
          </div>
          <div className="flex flex-col space-y-5">
            <Button
              type="primary"
              size="large"
              onClick={() => handleAcceptOrder(false)}
              className="px-8"
            >
              Accept & outsource delivery to Konnect
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => handleAcceptOrder(true)}
              className="px-8 bg-green-500 hover:bg-green-600 border-green-500 hover:border-green-600"
            >
              Accept & I'll handle delivery
            </Button>
          </div>
        </div>
      </Modal>

      {/* Ready Modal */}
      <Modal
        title="Mark Order as Ready"
        open={isReadyModal}
        onCancel={() => {
          setIsReadyModal(false);
          setBikerId("");
        }}
        footer={null}
        centered={true}
        width={500}
      >
        <div className="space-y-6">
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <h3 className="text-2xl font-semibold text-yellow-800 mb-3">
              ⚠️ Important Checklist
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-yellow-800">
              <li className="flex items-start">
                <span className="mr-2">✅</span>
                <span>YOU HAVE SEALED THE FOOD PACK</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✅</span>
                <span>YOU ATTACHED THE PICK-UP CODE ON THE BAG</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✅</span>
                <span>TIE THE PACKING BAG TO SECURE THE ORDER</span>
              </li>
            </ol>
          </div>

          {selectedOrder?.deliveryType?.toUpperCase() === "DELIVERY" &&
            selectedOrder?.delivery?.restaurantAcceptedDelivery && (
              <div className="space-y-2">
                <label className="text-gray-700 font-medium flex justify-between items-center">
                  <span>Delivery Person Name</span>
                  {!bikerId && (
                    <span className="text-red-500 text-xs font-normal">
                      Required for self-delivery
                    </span>
                  )}
                </label>
                <Select
                  placeholder="Select delivery person"
                  value={bikerId}
                  onChange={setBikerId}
                  size="large"
                  status={!bikerId ? "error" : ""}
                  style={{ width: "100%" }}
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {bikers.map((biker) => (
                    <Select.Option
                      key={biker.bikerId}
                      value={biker?.bikerId}
                      label={`${biker?.firstName} ${biker?.lastName} (${biker.accountId || biker.bikerId
                        })`}
                    >
                      {biker?.firstName} {biker?.lastName} (
                      {biker.accountId || biker.bikerId})
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate("/Restaurant/delivery")}
                  style={{ padding: 0, marginTop: 4 }}
                >
                  Add New Biker
                </Button>
              </div>
            )}


          <div className="flex flex-col items-center space-y-3">
            {!selectedOrder?.delivery?.restaurantAcceptedDelivery &&
              selectedOrder?.deliveryType != "PICKUP" &&
              selectedOrder?.delivery?.deliveryStatus === "WAITING" && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                  <p className="text-gray-700 mb-4">
                    System is waiting for Konnect Driver to accept the order.
                    What would you like to do?
                  </p>
                  <div className="flex flex-col space-y-2">
                    <Button
                      type="primary"
                      size="large"
                      onClick={() => {
                        setAcceptDeliveryModalVisible(true);
                        setIsReadyModal(false);
                      }}
                      className="bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700 w-full"
                    >
                      Can't wait, i'll sort delivery
                    </Button>
                    <Button
                      danger
                      size="large"
                      onClick={() => setRejectModalVisible(true)}
                      className="w-full"
                    >
                      Reject Order
                    </Button>
                  </div>
                </div>
              )}
            <Button
              type="primary"
              size="large"
              className="bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700 px-12"
              onClick={handleReadyClick}
              disabled={
                (!selectedOrder?.delivery?.restaurantAcceptedDelivery &&
                  selectedOrder?.deliveryType !== "PICKUP" &&
                  selectedOrder?.delivery?.deliveryStatus === "WAITING") ||
                (selectedOrder?.delivery?.restaurantAcceptedDelivery &&
                  selectedOrder?.deliveryType === "DELIVERY" &&
                  !bikerId)
              }
            >
              Mark as Ready
            </Button>
          </div>
        </div>
      </Modal>

      {/* Pickup Modal */}
      <Modal
        title="Enter Pickup Code"
        open={pickupModalVisible}
        onOk={confirmPickup}
        onCancel={() => {
          setPickupModalVisible(false);
          setPickupCode("");
          setSelectedPickupOrder(null);
        }}
        okText="Confirm Pickup"
        cancelText="Cancel"
        centered
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Input
            type="number"
            inputMode="numeric"
            placeholder="Enter pickup code"
            value={pickupCode}
            onChange={(e) => setPickupCode(e.target.value)}
            style={{ marginBottom: "16px" }}
          />
          {selectedPickupOrder && (
            <div>
              <p>
                <strong>Order ID:</strong> #
                {selectedPickupOrder.orderCode || selectedPickupOrder.track_id}
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Confirm Order Rejection"
        open={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        footer={null}
        centered={true}
        width={500}
      >
        <div className="text-center space-y-6">
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-2xl font-semibold text-red-900 mb-3">
              ⚠️ Reject Order?
            </p>
            <p className="text-gray-700">
              Are you sure you want to reject this order? This action cannot be
              undone.
            </p>
          </div>
          <div className="flex flex-col space-y-3">
            <Button
              danger
              size="large"
              onClick={confirmRejectOrder}
              className="px-8"
            >
              Yes, Reject Order
            </Button>
            <Button
              size="large"
              onClick={() => setRejectModalVisible(false)}
              className="px-8"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Accept Delivery Modal */}
      <Modal
        title="Confirm Accept Delivery"
        open={acceptDeliveryModalVisible}
        onCancel={() => setAcceptDeliveryModalVisible(false)}
        footer={null}
        centered={true}
        width={500}
      >
        <div className="text-center space-y-6">
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-2xl font-semibold text-green-900 mb-3">
              ✅ Accept & Deliver?
            </p>
            <p className="text-gray-700">
              You will handle the delivery for this order yourself. Do you want
              to continue?
            </p>
          </div>
          <div className="flex flex-col space-y-3">
            <Button
              type="primary"
              size="large"
              onClick={confirmAcceptDelivery}
              className="bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700 px-8"
            >
              Yes, Accept & Deliver
            </Button>
            <Button
              size="large"
              onClick={() => setAcceptDeliveryModalVisible(false)}
              className="px-8"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default OrderModals;
